/**
 * Hooks TanStack Query para el módulo de Carpeta Digital RRHH.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../../lib/apiClient'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FolderTemplate {
  id: string
  name: string
  parent: string | null
  description: string
  is_default: boolean
  order: number
  children: FolderTemplate[]
  created_at: string
  updated_at: string
}

export interface DocumentType {
  id: string
  name: string
  code: string
  requires_signature: boolean
  signature_type: 'simple' | 'advanced'
  default_folder: string | null
  default_folder_name: string | null
  retention_days: number | null
  description: string
  created_at: string
}

export interface EmployeeFolder {
  id: string
  employee: string
  template: string | null
  parent: string | null
  name: string
  order: number
  document_count: number
  children: EmployeeFolder[]
  created_at: string
}

export interface EmployeeDocument {
  id: string
  employee: string
  folder: string | null
  folder_name: string | null
  document_type: string | null
  document_type_name: string | null
  contract: string | null
  contract_label: string | null
  name: string
  description: string
  file_size: number
  mime_type: string
  status: 'active' | 'archived' | 'deleted'
  visible_to_employee: boolean
  uploaded_by: string | null
  uploaded_by_name: string | null
  uploaded_at: string
  archived_at: string | null
}

// ─── Helpers ────────────────────────────────────────────────────────────────

// apiClient ya tiene baseURL /api/v1/ — no duplicar el prefijo aquí.
// Para los downloads vía window.open() sí se usa la URL completa /api/v1/...
const BASE = '/hr-documents'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export { formatFileSize }

// ─── Folder Templates ────────────────────────────────────────────────────────

// El backend DRF aplica paginación global → `{count, next, previous, results: [...]}`.
// Estos helpers desempaquetan `.results` para mantener el contrato `T[]` con la UI.
type Paginated<T> = { count: number; next: string | null; previous: string | null; results: T[] }
const unwrap = <T,>(data: T[] | Paginated<T>): T[] =>
  Array.isArray(data) ? data : (data?.results ?? [])

export function useFolderTemplates() {
  return useQuery({
    queryKey: ['hr-folder-templates'],
    queryFn: async () => {
      const res = await apiClient.get<FolderTemplate[] | Paginated<FolderTemplate>>(`${BASE}/folder-templates/`)
      return unwrap(res.data)
    },
  })
}

export function useCreateFolderTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<FolderTemplate>) =>
      apiClient.post(`${BASE}/folder-templates/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-folder-templates'] }),
  })
}

export function useUpdateFolderTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<FolderTemplate> & { id: string }) =>
      apiClient.patch(`${BASE}/folder-templates/${id}/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-folder-templates'] }),
  })
}

export function useDeleteFolderTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`${BASE}/folder-templates/${id}/`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-folder-templates'] }),
  })
}

// ─── Document Types ──────────────────────────────────────────────────────────

export function useDocumentTypes() {
  return useQuery({
    queryKey: ['hr-document-types'],
    queryFn: async () => {
      const res = await apiClient.get<DocumentType[] | Paginated<DocumentType>>(`${BASE}/document-types/`)
      return unwrap(res.data)
    },
  })
}

export function useCreateDocumentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<DocumentType>) =>
      apiClient.post(`${BASE}/document-types/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-document-types'] }),
  })
}

export function useUpdateDocumentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<DocumentType> & { id: string }) =>
      apiClient.patch(`${BASE}/document-types/${id}/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-document-types'] }),
  })
}

export function useDeleteDocumentType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`${BASE}/document-types/${id}/`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-document-types'] }),
  })
}

// ─── Employee Folders ────────────────────────────────────────────────────────

export function useEmployeeFolders(employeeId: string) {
  return useQuery({
    queryKey: ['hr-employee-folders', employeeId],
    queryFn: async () => {
      const res = await apiClient.get<EmployeeFolder[] | Paginated<EmployeeFolder>>(
        `${BASE}/employees/${employeeId}/folders/`
      )
      return unwrap(res.data)
    },
    enabled: !!employeeId,
  })
}

export function useCreateEmployeeFolder(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; parent?: string | null; template?: string | null; order?: number }) =>
      apiClient.post(`${BASE}/employees/${employeeId}/folders/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-employee-folders', employeeId] }),
  })
}

export function useUpdateEmployeeFolder(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; order?: number }) =>
      apiClient.patch(`${BASE}/employees/${employeeId}/folders/${id}/`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-employee-folders', employeeId] }),
  })
}

export function useDeleteEmployeeFolder(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (folderId: string) =>
      apiClient.delete(`${BASE}/employees/${employeeId}/folders/${folderId}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-employee-folders', employeeId] }),
  })
}

// ─── Employee Documents ──────────────────────────────────────────────────────

export interface UseEmployeeDocumentsParams {
  employeeId: string
  folder?: string | null
  contract?: string | null
  status?: string
  search?: string
}

export function useEmployeeDocuments({ employeeId, folder, contract, status = 'active', search }: UseEmployeeDocumentsParams) {
  return useQuery({
    queryKey: ['hr-employee-documents', employeeId, { folder, contract, status, search }],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (folder) params.folder = folder
      else params.status = status
      if (status && folder) params.status = status
      if (contract) params.contract = contract
      if (search) params.search = search
      const res = await apiClient.get<{ documents: EmployeeDocument[]; count: number }>(
        `${BASE}/employees/${employeeId}/documents/`,
        { params }
      )
      return res.data
    },
    enabled: !!employeeId,
  })
}

/**
 * Emite el certificado de antigüedad del trabajador (#1491). Queda en su carpeta
 * «Certificados», verificable por QR. Si ya se emitió hoy, devuelve ese mismo.
 */
export function useEmitirCertificadoAntiguedad(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(`/hr-documents/employees/${employeeId}/certificado-antiguedad/`)
      return data as { document_id: string; name: string }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-employee-documents', employeeId] })
      qc.invalidateQueries({ queryKey: ['hr-employee-folders', employeeId] })
    },
  })
}

export function useUploadDocument(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (formData: FormData) =>
      apiClient.post(
        `${BASE}/employees/${employeeId}/documents/upload/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      ).then(r => r.data as EmployeeDocument),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-employee-documents', employeeId] })
      qc.invalidateQueries({ queryKey: ['hr-employee-folders', employeeId] })
    },
  })
}

export function useUpdateDocument(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<EmployeeDocument> & { id: string }) =>
      apiClient.patch(`${BASE}/documents/${id}/`, data).then(r => r.data as EmployeeDocument),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-employee-documents', employeeId] })
      qc.invalidateQueries({ queryKey: ['hr-employee-folders', employeeId] })
    },
  })
}

export function useDeleteDocument(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (docId: string) =>
      apiClient.delete(`${BASE}/documents/${docId}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-employee-documents', employeeId] })
      qc.invalidateQueries({ queryKey: ['hr-employee-folders', employeeId] })
    },
  })
}

export function useDownloadDocument() {
  return useMutation({
    mutationFn: async (docId: string): Promise<string> => {
      // El endpoint retorna 302 a una signed URL.
      // Hacemos GET con maxRedirects=0 para capturar la Location header,
      // o simplemente construimos la URL y abrimos en nueva pestaña.
      const url = `/api/v1/hr-documents/documents/${docId}/download/`
      // Abrir en nueva pestaña — el browser seguirá el redirect 302 automáticamente
      window.open(url, '_blank', 'noopener,noreferrer')
      return url
    },
  })
}
